import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TaskDashboard = ({ tasks, onUpdateTask, onDeleteTask, onCreateTask }) => {
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [viewMode, setViewMode] = useState('list');

  const statusOptions = [
    { value: 'all', label: 'All Tasks', icon: 'Layers' },
    { value: 'todo', label: 'To Do', icon: 'Circle' },
    { value: 'in_progress', label: 'In Progress', icon: 'Clock' },
    { value: 'completed', label: 'Completed', icon: 'CheckCircle2' }
  ];

  const priorityOptions = [
    { value: 'all', label: 'All Priorities' },
    { value: 'urgent', label: 'Urgent', color: 'error' },
    { value: 'high', label: 'High', color: 'warning' },
    { value: 'normal', label: 'Normal', color: 'primary' },
    { value: 'low', label: 'Low', color: 'muted' }
  ];

  const filteredTasks = useMemo(() => {
    return tasks?.filter(task => {
      const statusMatch = filterStatus === 'all' || task?.status === filterStatus;
      const priorityMatch = filterPriority === 'all' || task?.priority === filterPriority;
      return statusMatch && priorityMatch;
    });
  }, [tasks, filterStatus, filterPriority]);

  const getStatusColor = (status) => {
    const colors = {
      todo: 'bg-muted text-muted-foreground',
      in_progress: 'bg-warning/20 text-warning',
      completed: 'bg-success/20 text-success',
      cancelled: 'bg-error/20 text-error'
    };
    return colors?.[status] || colors?.todo;
  };

  const getPriorityIcon = (priority) => {
    const icons = {
      urgent: 'AlertCircle',
      high: 'ArrowUp',
      normal: 'Minus',
      low: 'ArrowDown'
    };
    return icons?.[priority] || 'Minus';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      urgent: 'var(--color-error)',
      high: 'var(--color-warning)',
      normal: 'var(--color-primary)',
      low: 'var(--color-muted-foreground)'
    };
    return colors?.[priority] || colors?.normal;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No due date';
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow?.setDate(tomorrow?.getDate() + 1);

    if (date?.toDateString() === today?.toDateString()) return 'Today';
    if (date?.toDateString() === tomorrow?.toDateString()) return 'Tomorrow';
    
    return date?.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date() && new Date(dueDate)?.toDateString() !== new Date()?.toDateString();
  };

  const taskStats = useMemo(() => {
    const total = tasks?.length || 0;
    const completed = tasks?.filter(t => t?.status === 'completed')?.length || 0;
    const inProgress = tasks?.filter(t => t?.status === 'in_progress')?.length || 0;
    const overdue = tasks?.filter(t => isOverdue(t?.due_date) && t?.status !== 'completed')?.length || 0;
    
    return { total, completed, inProgress, overdue };
  }, [tasks]);

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Layers" size={18} color="var(--color-primary)" />
            <p className="text-sm text-muted-foreground">Total Tasks</p>
          </div>
          <p className="text-2xl font-semibold text-foreground">{taskStats?.total}</p>
        </div>

        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Clock" size={18} color="var(--color-warning)" />
            <p className="text-sm text-muted-foreground">In Progress</p>
          </div>
          <p className="text-2xl font-semibold text-foreground">{taskStats?.inProgress}</p>
        </div>

        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="CheckCircle2" size={18} color="var(--color-success)" />
            <p className="text-sm text-muted-foreground">Completed</p>
          </div>
          <p className="text-2xl font-semibold text-foreground">{taskStats?.completed}</p>
        </div>

        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="AlertCircle" size={18} color="var(--color-error)" />
            <p className="text-sm text-muted-foreground">Overdue</p>
          </div>
          <p className="text-2xl font-semibold text-error">{taskStats?.overdue}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-card rounded-lg border border-border p-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-2 overflow-x-auto">
            {statusOptions?.map((option) => (
              <button
                key={option?.value}
                onClick={() => setFilterStatus(option?.value)}
                className={`
                  flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-250 whitespace-nowrap
                  ${filterStatus === option?.value
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }
                `}
              >
                <Icon name={option?.icon} size={16} />
                {option?.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e?.target?.value)}
              className="px-3 py-2 rounded-lg border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {priorityOptions?.map((option) => (
                <option key={option?.value} value={option?.value}>
                  {option?.label}
                </option>
              ))}
            </select>

            <div className="flex gap-1 border border-input rounded-lg p-1">
              <button
                onClick={() => setViewMode('list')}
                className={`
                  p-2 rounded transition-colors
                  ${viewMode === 'list' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}
                `}
              >
                <Icon name="List" size={18} />
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`
                  p-2 rounded transition-colors
                  ${viewMode === 'grid' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}
                `}
              >
                <Icon name="Grid" size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Task List */}
      {filteredTasks?.length === 0 ? (
        <div className="bg-card rounded-lg border border-border p-12 text-center">
          <Icon name="CheckSquare" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
          <p className="text-muted-foreground mb-4">No tasks found</p>
          <Button onClick={onCreateTask} className="flex items-center gap-2 mx-auto">
            <Icon name="Plus" size={18} />
            Create Your First Task
          </Button>
        </div>
      ) : (
        <div className={viewMode === 'grid' ? 'grid md:grid-cols-2 lg:grid-cols-3 gap-4' : 'space-y-3'}>
          {filteredTasks?.map((task) => (
            <div
              key={task?.id}
              className="bg-card rounded-lg border border-border p-4 hover:border-primary/50 transition-all duration-250"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-start gap-3 flex-1">
                  <button
                    onClick={() => onUpdateTask(task?.id, {
                      status: task?.status === 'completed' ? 'todo' : 'completed'
                    })}
                    className="mt-1"
                  >
                    <Icon
                      name={task?.status === 'completed' ? 'CheckCircle2' : 'Circle'}
                      size={20}
                      color={task?.status === 'completed' ? 'var(--color-success)' : 'var(--color-muted-foreground)'}
                    />
                  </button>
                  <div className="flex-1">
                    <h3 className={`font-semibold text-foreground mb-1 ${task?.status === 'completed' ? 'line-through opacity-60' : ''}`}>
                      {task?.title}
                    </h3>
                    {task?.description && (
                      <p className="text-sm text-muted-foreground mb-2">{task?.description}</p>
                    )}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(task?.status)}`}>
                        {task?.status?.replace('_', ' ')}
                      </span>
                      <div className="flex items-center gap-1">
                        <Icon name={getPriorityIcon(task?.priority)} size={14} color={getPriorityColor(task?.priority)} />
                        <span className="text-xs text-muted-foreground capitalize">{task?.priority}</span>
                      </div>
                      {task?.due_date && (
                        <div className={`flex items-center gap-1 ${isOverdue(task?.due_date) && task?.status !== 'completed' ? 'text-error' : 'text-muted-foreground'}`}>
                          <Icon name="Calendar" size={14} />
                          <span className="text-xs">{formatDate(task?.due_date)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => onDeleteTask(task?.id)}
                  className="p-1 rounded hover:bg-error/10 transition-colors"
                >
                  <Icon name="Trash2" size={16} color="var(--color-error)" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TaskDashboard;